/**
 * Recent conversations list. Paginated 8-per-page, prev/next chevrons +
 * "Page X of Y" indicator at the bottom — mirrors iOS RecentsView.
 * Tap a row to resume the chat. The × button soft-archives.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  ChevronLeft,
  ChevronRight,
  ICON_STROKE,
  NavIcons,
  X,
} from "@/shared/components/icons";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { archiveConversation, listConversations } from "@/shared/api";
import { relativeTime } from "@/shared/format";
import type { ConversationSummary } from "@/shared/models";

const PAGE_SIZE = 8;

export function Recent() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ["conversations"],
    queryFn: listConversations,
  });

  const archive = useMutation({
    mutationFn: (id: string) => archiveConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });

  const conversations = data ?? [];
  const totalPages = Math.max(
    1,
    Math.ceil(conversations.length / PAGE_SIZE),
  );

  // If the user archived the last item on the last page, drop to the
  // previous page so they don't land on an empty view.
  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  const visibleStart = currentPage * PAGE_SIZE;
  const visible = conversations.slice(
    visibleStart,
    visibleStart + PAGE_SIZE,
  );

  return (
    <div className="flex flex-col h-[calc(100dvh-3rem-5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] md:h-screen">
      <div className="max-w-3xl w-full mx-auto px-4 md:px-8 py-8 flex-1 overflow-y-auto scroll-momentum">
        <div className="mb-6">
          <PageHeader
            Icon={NavIcons.recent}
            title="Recent"
            action={
              data ? (
                <span className="text-xs text-text-muted">
                  {data.length} conversation{data.length === 1 ? "" : "s"}
                </span>
              ) : undefined
            }
          />
        </div>

        {isLoading && <div className="text-text-muted">Loading…</div>}
        {error && (
          <div className="text-sm text-danger">
            Couldn't load conversations:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </div>
        )}

        <ul className="space-y-2">
          {visible.map((c) => (
            <ConversationRow
              key={c.id}
              conversation={c}
              onArchive={() => archive.mutate(c.id)}
              disabled={archive.isPending}
            />
          ))}
        </ul>

        {!isLoading && conversations.length === 0 && <EmptyState />}
      </div>

      {totalPages > 1 && (
        <PagerBar
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((p) => Math.max(0, p - 1))}
          onNext={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
        />
      )}
    </div>
  );
}

function ConversationRow({
  conversation: c,
  onArchive,
  disabled,
}: {
  conversation: ConversationSummary;
  onArchive: () => void;
  disabled: boolean;
}) {
  return (
    <li className="rounded-xl border border-border bg-surface px-4 py-3 hover:border-accent/50 transition">
      <div className="flex items-center justify-between gap-3">
        <Link to={`/chat/${c.id}`} className="flex-1 min-w-0 group">
          <div className="font-medium text-text group-hover:text-accent transition truncate">
            {c.title || "Untitled"}
          </div>
          <div className="text-xs text-text-muted mt-0.5">
            {c.messageCount} message{c.messageCount === 1 ? "" : "s"} ·{" "}
            {relativeTime(c.updatedAt)}
          </div>
        </Link>
        <button
          type="button"
          onClick={onArchive}
          disabled={disabled}
          className="w-11 h-11 rounded-lg flex items-center justify-center text-text-muted/60 hover:text-danger hover:bg-danger/10 transition disabled:opacity-50"
          aria-label="Archive conversation"
        >
          <X size={16} strokeWidth={ICON_STROKE} />
        </button>
      </div>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="text-center text-text-muted py-12">
      No conversations yet. Start one from{" "}
      <Link to="/drills" className="text-accent hover:underline">
        Drills
      </Link>
      .
    </div>
  );
}

function PagerBar({
  currentPage,
  totalPages,
  onPrev,
  onNext,
}: {
  currentPage: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const isFirst = currentPage === 0;
  const isLast = currentPage >= totalPages - 1;
  return (
    <div className="border-t border-border bg-surface/80 backdrop-blur">
      <div className="max-w-3xl w-full mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
        <button
          type="button"
          onClick={onPrev}
          disabled={isFirst}
          aria-label="Previous page"
          className={[
            "w-11 h-11 rounded-lg flex items-center justify-center transition",
            isFirst
              ? "text-text-muted/30 cursor-not-allowed"
              : "text-text hover:bg-text/5",
          ].join(" ")}
        >
          <ChevronLeft size={18} strokeWidth={ICON_STROKE} />
        </button>
        <div className="flex-1 text-center text-sm text-text-muted font-medium tabular-nums">
          Page {currentPage + 1} of {totalPages}
        </div>
        <button
          type="button"
          onClick={onNext}
          disabled={isLast}
          aria-label="Next page"
          className={[
            "w-11 h-11 rounded-lg flex items-center justify-center transition",
            isLast
              ? "text-text-muted/30 cursor-not-allowed"
              : "text-text hover:bg-text/5",
          ].join(" ")}
        >
          <ChevronRight size={18} strokeWidth={ICON_STROKE} />
        </button>
      </div>
    </div>
  );
}

